import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowRight, Sparkles, X } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import {
  V2_APP_URL,
  dismissV2Invite,
  isV2InviteDismissed,
  v2InviteAllowed,
  v2InviteAudience,
  v2InviteShownOn,
} from '../lib/v2Invite';

interface V2InviteBannerProps {
  /** From the layout's own `useAdmin`, so the lookup is not made twice. */
  isAdmin: boolean;
  adminLoading: boolean;
}

/**
 * A quiet invitation to SermonBuild 2.0, above the page content.
 *
 * Hidden until it is sure: nothing renders while the "already in 2.0" check
 * is in flight, and an error in that check hides it rather than shows it.
 * Both pieces of state are keyed by user id, so signing in as someone else
 * never inherits the previous account's answer.
 */
export function V2InviteBanner({ isAdmin, adminLoading }: V2InviteBannerProps) {
  const { user } = useAuth();
  const location = useLocation();
  const allowed = v2InviteAllowed(v2InviteAudience(), isAdmin);

  const [dismissedFor, setDismissedFor] = useState<string | null>(null);
  const [moved, setMoved] = useState<{ userId: string; moved: boolean } | null>(null);

  const userId = user?.id ?? null;
  const dismissed = userId !== null && (dismissedFor === userId || isV2InviteDismissed(userId));

  useEffect(() => {
    if (!userId || !allowed || dismissed) return;
    let cancelled = false;
    // A pastor with any sermon in 2.0 has already moved. `user_id` names the
    // owner: RLS also lets team members read documents shared with them, and
    // those must not count as this pastor having moved.
    supabase
      .from('sermon_documents')
      .select('id')
      .eq('user_id', userId)
      .limit(1)
      .then(({ data, error }) => {
        if (!cancelled) setMoved({ userId, moved: error ? true : (data?.length ?? 0) > 0 });
      });
    return () => {
      cancelled = true;
    };
  }, [userId, allowed, dismissed]);

  if (!userId || adminLoading || !allowed || dismissed) return null;
  if (moved?.userId !== userId || moved.moved) return null;
  if (!v2InviteShownOn(location.pathname)) return null;

  const dismiss = () => {
    dismissV2Invite(userId);
    setDismissedFor(userId);
  };

  return (
    <div className="border-b border-sand dark:border-slate/40 bg-sand/40 dark:bg-charcoal">
      <div className="flex items-start gap-3 px-4 py-3 sm:items-center sm:px-6">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-electric dark:text-soft-white sm:mt-0" aria-hidden="true" />
        <p className="flex-1 text-sm text-slate dark:text-soft-white">
          <span className="font-semibold text-charcoal dark:text-white">SermonBuild 2.0 is ready to try.</span>{' '}
          Same login, and your sermons are already there. You can come back here anytime.
        </p>
        <a
          href={V2_APP_URL}
          className="inline-flex shrink-0 items-center gap-1 rounded-md bg-electric px-3 py-1.5 text-sm font-medium text-white hover:bg-electric/90"
        >
          Try 2.0
          <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
        </a>
        <button
          type="button"
          onClick={dismiss}
          className="shrink-0 rounded p-1 text-slate hover:text-charcoal dark:text-soft-white dark:hover:text-white"
          aria-label="Dismiss the SermonBuild 2.0 invitation"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
